import { createContext, use, useMemo } from "react";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
import * as SelectPrimitive from "@radix-ui/react-select";
//#region src/single-select/SingleSelect.tsx
const SingleSelectGroup = SelectPrimitive.Group;
const SingleSelectItemText = SelectPrimitive.ItemText;
const SingleSelectContext = createContext({ isError: false });
const SingleSelectRoot = ({ error = false, triggerRef, ariaDescribedBy, ...restProps }) => {
	const ctx = useMemo(() => ({
		isError: error,
		triggerRef,
		ariaDescribedBy
	}), [
		error,
		triggerRef,
		ariaDescribedBy
	]);
	return /* @__PURE__ */ jsx(SingleSelectContext, {
		value: ctx,
		children: /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...restProps })
	});
};
const SingleSelectTrigger = ({ size = "md", className, children, ...rest }) => {
	const { isError, triggerRef, ariaDescribedBy } = use(SingleSelectContext);
	return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
		ref: triggerRef,
		"data-givebutter-primitive": "single-select",
		"aria-invalid": isError || void 0,
		"aria-describedby": ariaDescribedBy,
		className: twMerge("group/trigger relative flex h-full w-full min-w-0 shrink-0 cursor-pointer appearance-none items-center justify-between gap-2 rounded-lg border-none bg-white px-3 py-2 text-base leading-4 text-gray-900 outline-hidden", "ring-1 ring-gray-300 ring-inset", "focus:ring-2 focus:ring-blue-500 radix-state-open:ring-2 radix-state-open:ring-blue-500", "radix-disabled:cursor-not-allowed radix-disabled:bg-gray-50 radix-disabled:text-gray-900", isError && "ring-2 ring-red-500", size === "sm" && "h-8 text-sm", size === "md" && "h-10", size === "lg" && "h-12", className),
		...rest,
		children: [/* @__PURE__ */ jsx("div", {
			className: "truncate leading-6",
			children: children || /* @__PURE__ */ jsx(SingleSelectValue, {})
		}), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
			asChild: true,
			children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
				icon: faChevronDown,
				className: "text-xs group-radix-state-open/trigger:rotate-180"
			})
		})]
	});
};
const SingleSelectValue = ({ className, placeholder = "Select", ...rest }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Value, {
		className,
		placeholder,
		...rest
	});
};
const SingleSelectContent = ({ className, children, sideOffset = 4, ...rest }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(SelectPrimitive.Content, {
		position: "popper",
		sideOffset,
		className: twMerge("relative z-pentOutermost m-0 max-h-72 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border-none bg-white shadow-md ring-1 ring-gray-200", className),
		...rest,
		children: [
			/* @__PURE__ */ jsx(SelectScrollUpButton, {}),
			/* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
				className: "flex flex-col p-1",
				children
			}),
			/* @__PURE__ */ jsx(SelectScrollDownButton, {})
		]
	}) });
};
const SelectScrollUpButton = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, {
	className: twMerge("absolute top-0 right-0 left-0 z-10 flex cursor-default items-center justify-center bg-linear-to-b from-white to-transparent py-1", className),
	...props,
	children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
		icon: faChevronUp,
		className: "size-3"
	})
});
const SelectScrollDownButton = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, {
	className: twMerge("absolute right-0 bottom-0 left-0 z-10 flex cursor-default items-center justify-center bg-linear-to-t from-white to-transparent py-1", className),
	...props,
	children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
		icon: faChevronDown,
		className: "size-3"
	})
});
const SingleSelectLabel = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Label, {
		className: twMerge("px-2 py-1.5 text-sm font-medium text-gray-600", className),
		...rest
	});
};
const SingleSelectItem = ({ className, children, ...rest }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Item, {
		className: twMerge("flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm leading-4 font-medium outline-hidden", "focus:bg-gray-100", "radix-disabled:cursor-not-allowed radix-disabled:text-gray-500", className),
		...rest,
		children
	});
};
const SingleSelectNoneFoundText = ({ children, className, ...rest }) => {
	return /* @__PURE__ */ jsx("span", {
		role: "option",
		"aria-disabled": true,
		"aria-selected": false,
		className: twMerge("block p-1.5", className),
		...rest,
		children: children ?? "No items found"
	});
};
const SingleSelectSeparator = ({ className, children, ...rest }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Separator, {
		className: twMerge("-mx-1 my-1 h-px min-h-px bg-gray-100", className),
		...rest
	});
};
const Root = SingleSelectRoot;
const Group = SingleSelectGroup;
const Value = SingleSelectValue;
const Trigger = SingleSelectTrigger;
const Content = SingleSelectContent;
const Label = SingleSelectLabel;
const Item = SingleSelectItem;
const ItemText = SingleSelectItemText;
const NoneFoundText = SingleSelectNoneFoundText;
const Separator = SingleSelectSeparator;
//#endregion
export { Content, Group, Item, ItemText, Label, NoneFoundText, Root, Separator, SingleSelectContent, SingleSelectContext, SingleSelectGroup, SingleSelectItem, SingleSelectItemText, SingleSelectLabel, SingleSelectNoneFoundText, SingleSelectRoot, SingleSelectSeparator, SingleSelectTrigger, Trigger, Value };
